import { playVideoVod, setPlayerControlsState, Translation } from 'common/actions';
import { HAS_ACCESS } from 'common/config/constants';
import variables from 'common/config/variables';
import { Epg, LiveCategories } from 'common/constants/data-types';
import { IChannelList } from 'common/reducers/channelList';
import { IVideoPlayer, PlayerControlsState } from 'common/reducers/videoPlayer';
import { getClassName } from 'common/utils';
import AnimationDurations from 'common/utils/AnimationDurations';
import { isEmpty } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import ChannelList from './ChannelList';
import { PlayerControlsProps } from './Controls';
import './Controls.styles';
import DetailedInfo from './DetailedInfo';
import ExtendedBottomView from './ExtendedBottomView';
import LiveCategoriesStrip from './LiveCategoriesStrip';
import Seekbar from './Seekbar';
import SeekButtonsStripe from './SeekButtonsStripe';
import VodPlayButtons from './VodPlayButtons';

interface BottomViewProps extends PlayerControlsProps {
  // Redux injected
  i18n: Translation;
  playerControlsState: PlayerControlsState;
  setPlayerControlsState: (playerState: PlayerControlsState) => void;
  playVideoVod: (currentepg: Epg, options?: any) => void;
  getDuration: () => number;
  loadedChannelEpgs?: boolean;
  loadedCurrentEpg?: boolean;
  currentEpg?: Epg;
  hasNextVideo?: boolean;
  fastZapping?: boolean;
  channelAccess?: number;
  videoLocked?: boolean;
  vodAsset?: Epg;
  isVideoStopped?: boolean;
  //
  fastZapNumber?: number;
  channelList: IChannelList;
  toggleControls: PlayerControlsProps['toggleControls'];
  vodSelectionPage?: boolean;
}
function BottomView(props: BottomViewProps) {
  const bottomViewOpennedRef = React.useRef(false);
  const bottomViewOpenned = useMemo(() => {
    bottomViewOpennedRef.current = ['bottom-view'].includes(props.playerControlsState);
    return bottomViewOpennedRef.current;
  }, [props.playerControlsState]);
  // Hide bottom view when hidden and fast zapping
  const visible = useMemo(() => {
    const state = props.playerControlsState;
    switch (state) {
      case 'mini':
      case 'bottom-view':
      case 'expanded':
        return !props.hasNextVideo && props.channelAccess === HAS_ACCESS && !props.disabled;
      case 'channels':
      case 'seeking':
        return true;
      case 'hidden':
        return false;
      default:
        return false;
    }
  }, [props.playerControlsState, props.loadedChannelEpgs, props.hasNextVideo, props.disabled]);

  const extendedBottomViewVisible = useMemo(
    () => ['expanded', 'bottom-view'].includes(props.playerControlsState),
    [props.playerControlsState]
  );
  const extendedBottomViewClosed = useMemo(
    () => ['hidden', 'mini'].includes(props.playerControlsState),
    [props.playerControlsState]
  );

  const onBottomViewFocus = useCallback(() => props.setPlayerControlsState('bottom-view'), []);

  const onBottomViewBlur = useCallback(() => {
    bottomViewOpennedRef.current && props.toggleControls();
  }, []);

  const onBottomViewPlay = useCallback(
    () =>
      setTimeout(() => {
        props.toggleControls(true);
      }, variables.SHORT_THROTTLE),
    []
  );

  const vodEpg = useMemo(() => {
    return props.vodSelectionPage && props.currentEpg;
  }, [props.currentEpg, props.vodSelectionPage]);

  const onVodPlayButtonPress = useCallback(() => {
    if (!vodEpg) return;
    if (props.isVideoStopped) {
      props.togglePlay();
      setTimeout(() => {
        AnimationDurations.set('PLAYER_AUTOHIDE_TIMER', variables.PLAYER_MINIVIEW_AUTOHIDE_TIMER);
        props.toggleControls();
      }, variables.SHORT_THROTTLE);
    } else {
      props.playVideoVod(vodEpg, { isFullscreen: true });
      AnimationDurations.set('PLAYER_AUTOHIDE_TIMER', variables.PLAYER_MINIVIEW_AUTOHIDE_TIMER);
    }
  }, [vodEpg, props.isVideoStopped]);

  const onVodBeginingPress = useCallback(() => {
    props.setVideoToBegining();
    props.togglePlay();
    AnimationDurations.set('PLAYER_AUTOHIDE_TIMER', variables.PLAYER_MINIVIEW_AUTOHIDE_TIMER);
    setTimeout(() => {
      props.toggleControls();
    }, variables.SHORT_THROTTLE);
  }, []);

  return (
    <div
      className={getClassName('bottom-view ', {
        visible,
        'bottom-view-visible': bottomViewOpenned
      })}
    >
      <ChannelList fastZapNumber={props.fastZapNumber} />
      <div className="bottom-view-controls-container">
        <div
          className={getClassName('fast-zapping-container', { 'fast-zapping': props.fastZapping })}
        >
          <LiveCategoriesStrip visible={props.playerControlsState === 'channels'} />
          {props.playerControlsState !== 'seeking' && (
            <DetailedInfo
              bottomViewVisible={bottomViewOpenned}
              vodSelectionPage={props.vodSelectionPage}
            />
          )}
          <div style={{ position: 'relative' }}>
            {!bottomViewOpenned && (
              <Seekbar
                focusKey="SEEKBAR"
                pause={props.pause}
                play={props.play}
                setCurrentTime={props.setCurrentTime}
                getDuration={props.getDuration}
                hidden={props.vodSelectionPage}
              />
            )}

            <SeekButtonsStripe
              togglePlay={props.togglePlay}
              forward={props.forward}
              backward={props.backward}
              setVideoToLive={props.setVideoToLive}
              setVideoToBegining={props.setVideoToBegining}
              toggleControls={props.toggleControls}
              getDuration={props.getDuration}
              visible={!props.vodSelectionPage}
            />
          </div>
        </div>
        <VodPlayButtons
          visible={props.vodSelectionPage}
          onPlay={onVodPlayButtonPress}
          hidden={bottomViewOpenned}
          onBeginning={onVodBeginingPress}
          currentEpg={vodEpg}
          epgs={props.vodAsset?.epgs}
        />
        <ExtendedBottomView
          visible={extendedBottomViewVisible}
          closed={extendedBottomViewClosed}
          onFocus={onBottomViewFocus}
          onBlur={onBottomViewBlur}
          onPlay={onBottomViewPlay}
        />
      </div>
    </div>
  );
}

const mapStateToProps = ({
  videoPlayer,
  channelList,
  liveCategories,
  logos,
  i18n
}: {
  videoPlayer: IVideoPlayer;
  channelList: IChannelList;
  liveCategories: LiveCategories;
  logos: any;
  i18n: Translation;
}) => ({
  playerControlsState: videoPlayer.playerControlsState,
  loadedChannelEpgs: !isEmpty(videoPlayer.allEpgs) && !isEmpty(videoPlayer.epgs),
  loadedCurrentEpg: videoPlayer.epgs && !isEmpty(videoPlayer.epgs.current),
  currentEpg: videoPlayer.epgs?.current,
  vodAsset: videoPlayer.video?.vodAsset,
  isVideoStopped: videoPlayer.isStopped,
  hasNextVideo: !isEmpty(videoPlayer.nextVideo),
  videoLocked: videoPlayer.video?.locked,
  fastZapping: videoPlayer.video?.fastZapping,
  channelAccess: videoPlayer.video?.access,
  channelList,
  liveCategories,
  logos,
  i18n
});

export default React.memo(
  connect(mapStateToProps, { setPlayerControlsState, playVideoVod })(BottomView)
);
